import { ref } from 'vue'
import type { Ref } from 'vue'
import type { TeamMember } from '@/types/team'
import {
  listMembers as apiListMembers,
  updateMemberRole as apiUpdateMemberRole,
  removeMember as apiRemoveMember,
} from '@/api/members'

const members = ref<TeamMember[]>([])
const loading = ref(false)
const saving = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}

async function fetchMembers(organisationId: number): Promise<void> {
  loading.value = true
  generalError.value = null
  try {
    members.value = await apiListMembers(organisationId)
  } catch {
    generalError.value = 'team.errors.load_members_failed'
  } finally {
    loading.value = false
  }
}

async function updateMemberRole(
  organisationId: number,
  userId: number,
  role: 'owner' | 'admin',
): Promise<TeamMember | null> {
  saving.value = true
  errors.value = {}
  generalError.value = null
  try {
    const updated = await apiUpdateMemberRole(organisationId, userId, { role })
    members.value = members.value.map((m) => (m.id === userId ? updated : m))
    return updated
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'team.errors.update_role_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

async function removeMember(organisationId: number, userId: number): Promise<boolean> {
  saving.value = true
  generalError.value = null
  try {
    await apiRemoveMember(organisationId, userId)
    members.value = members.value.filter((m) => m.id !== userId)
    return true
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'team.errors.remove_member_failed'
    }
    return false
  } finally {
    saving.value = false
  }
}

export function useTeamMembers(): {
  members: Ref<TeamMember[]>
  loading: Ref<boolean>
  saving: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  generalError: Ref<string | null>
  fetchMembers: (organisationId: number) => Promise<void>
  updateMemberRole: (
    organisationId: number,
    userId: number,
    role: 'owner' | 'admin',
  ) => Promise<TeamMember | null>
  removeMember: (organisationId: number, userId: number) => Promise<boolean>
} {
  return {
    members,
    loading,
    saving,
    errors,
    generalError,
    fetchMembers,
    updateMemberRole,
    removeMember,
  }
}
